import {
  Accordion,
  Button,
  EmptyState,
  Icon,
  useForm,
  useHookFormState,
  type Control,
  type FieldValues,
  type UseFormReturn,
} from "@validereinc/common-components";
import classNames from "classnames/bind";
import { get } from "es-toolkit/compat";
import { nanoid } from "nanoid";
import React, { useMemo, useRef, useState, type HTMLAttributes } from "react";
import { create } from "zustand";
import styles from "./ReactHookFormDevtools.module.css";

export type useReactHookFormDevtoolsType = {
  control: Control<FieldValues> | null;
  setControl: (control: Control<FieldValues>) => void;
  formControl: Omit<
    UseFormReturn<Record<string, any>, any, FieldValues>,
    "formState"
  > | null;
  setFormControl: (
    control: Omit<
      UseFormReturn<Record<string, any>, any, FieldValues>,
      "formState"
    >
  ) => void;
  clear: () => void;
};

export const useReactHookFormDevtools = create<useReactHookFormDevtoolsType>(
  (set) => ({
    control: null,
    setControl: (control) => {
      set({ control });
    },
    formControl: null,
    setFormControl: (control) => {
      set({ formControl: control });
    },
    clear: () => set({ control: null, formControl: null }),
  })
);

export const useFormInspect = <T extends FieldValues = FieldValues>() =>
  // ...params: Parameters<typeof useForm<T>>
  {
    /* TODO: react-hook-form >= 7.55.0 introduces performance regressions when clearing filters and creating lookup filters.
  const formControls = useRef(createFormControl(...params));
  const form = useForm<T>({
    formControl: formControls.current.formControl,
  });
  const setControl = useReactHookFormDevtools((s) => s.setControl);
  const setFormControl = useReactHookFormDevtools((s) => s.setFormControl);

  useEffect(() => {
    // @ts-expect-error store type cannot be reasonably typed
    setControl(formControls.current.control);
    // @ts-expect-error store type cannot be reasonably typed
    setFormControl(formControls.current.formControl);
  }, []);

  return form;
  */

    return useForm<T>();
  };

const cx = classNames.bind(styles);

const ReactHookFormDevtoolsContent = ({
  className,
  ...restProps
}: HTMLAttributes<HTMLDivElement>) => {
  const control = useReactHookFormDevtools((s) => s.control);
  const formControl = useReactHookFormDevtools((s) => s.formControl);
  const clear = useReactHookFormDevtools((s) => s.clear);
  const formState = useHookFormState({
    control: control ?? undefined, // this will throw when undefined
  });
  const [, setShouldRefresh] = useState({});
  const [search, setSearch] = useState("");
  const instanceId = useRef(nanoid(8));

  const formStateProperties = useMemo(
    () => Object.entries(Object.getOwnPropertyDescriptors(formState ?? {})),
    [formState]
  );

  const renderedFormStateProperties = useMemo(
    () =>
      formStateProperties.map(([stateName, descriptor]) => {
        const stateValue = descriptor.get
          ? descriptor.get.call(formState)
          : descriptor.value;

        return (
          <PropertyListItem
            key={stateName}
            componentId={instanceId.current}
            propertyName={stateName}
            propertyValue={stateValue}
          />
        );
      }),
    [formStateProperties, formState]
  );

  const inputNames = useMemo(
    () =>
      Array.from(control?._names.mount ?? new Set<string>()).filter((name) =>
        search ? name.toLowerCase().includes(search.toLowerCase()) : true
      ),
    [JSON.stringify(control?._names.mount ?? []), search]
  );

  return (
    <>
      <div
        className={cx("content-container", className)}
        aria-label="React Hook Form Development Tools Panel"
        {...restProps}
      >
        <div className={cx("content-header")}>
          <input
            type="search"
            name="search"
            placeholder="Search by name..."
            className={cx("input-search")}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button
            variant="outline"
            type="button"
            onClick={() => setShouldRefresh({})}
          >
            Refresh
          </Button>
          <Button
            variant="error"
            type="button"
            onClick={clear}
          >
            Unmount
          </Button>
        </div>
        <div className={cx("content-body")}>
          {control !== null ? (
            <>
              <Accordion>
                <Accordion.AccordionPanel
                  title="Form State"
                  dataKey="form"
                  className={cx("accordion-panel")}
                >
                  <div className={cx("group")}>
                    <ul className={cx("property-list")}>
                      {renderedFormStateProperties}
                    </ul>
                    <div className={cx("actions-container")}>
                      <Button
                        variant="outline"
                        type="button"
                        // eslint-disable-next-line no-console
                        onClick={() => console.log(formState ?? {})}
                      >
                        Log State
                      </Button>
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() =>
                          // eslint-disable-next-line no-console
                          console.log(formControl?.getValues() ?? {})
                        }
                      >
                        Log Values
                      </Button>
                      <Button
                        variant="error-outline"
                        type="button"
                        onClick={() => control?._reset()}
                      >
                        Reset
                      </Button>
                      <Button
                        variant="error-outline"
                        type="button"
                        onClick={() =>
                          control?._reset(undefined, {
                            keepDefaultValues: true,
                          })
                        }
                      >
                        Reset but keep defaults
                      </Button>
                    </div>
                  </div>
                </Accordion.AccordionPanel>
              </Accordion>
              <Accordion>
                {inputNames?.length ? (
                  inputNames.map((name) => (
                    <Accordion.AccordionPanel
                      key={name}
                      dataKey={name}
                      title={name}
                      className={cx("accordion-panel")}
                    >
                      <ReactHookFormInputValue name={name} />
                    </Accordion.AccordionPanel>
                  ))
                ) : search ? (
                  <EmptyState
                    icon={<Icon variant="question" />}
                    title="No inputs match your search query"
                    suggestion={
                      (control?._names.mount ?? new Set<string>()).size
                        ? "Other inputs do exist so try adjusting your query."
                        : "There are no inputs to search."
                    }
                    className={cx("empty-state")}
                  />
                ) : (
                  <EmptyState
                    icon={<Icon variant="gear-fine" />}
                    title="No inputs available"
                    suggestion="Are there any inputs registered to the form?"
                    className={cx("empty-state")}
                  />
                )}
              </Accordion>
            </>
          ) : (
            <EmptyState
              icon={<Icon variant="gear-fine" />}
              title="No form instance connected"
              suggestion="Instantiate a form with useFormInspect() instead of useForm() somewhere in the codebase to see the details of the form when it's rendered here."
              className={cx("empty-state")}
            />
          )}
        </div>
      </div>
    </>
  );
};

function ReactHookFormInputValue({ name }: { name: string }) {
  const control = useReactHookFormDevtools((s) => s.control);
  const formControl = useReactHookFormDevtools((s) => s.formControl);

  const values = formControl?.getValues() ?? {};
  const value = useMemo(
    () => get(values, name),
    [JSON.stringify(values), name]
  );

  return (
    <>
      <label
        className={cx("property-label", "block")}
        id={`${name}-value`}
      >
        Value
      </label>
      <div
        className={cx("property-value-container")}
        aria-labelledby={`${name}-value`}
      >
        <pre
          className={cx(
            "property-value",
            "block",
            value === undefined && "not-defined"
          )}
        >
          {value !== undefined
            ? JSON.stringify(get(values, name))
            : "undefined"}
        </pre>
      </div>
      <p className={cx("group-title")}>Form Field State</p>
      <ul className={cx("property-list")}>
        {Object.entries(control?.getFieldState(name) ?? {}).map(
          ([stateName, stateValue]) => (
            <li
              key={stateName}
              className={cx("property-list-item")}
            >
              <label
                className={cx("property-label")}
                id={`${name}-${stateName}`}
              >
                {stateName}
              </label>
              <pre
                className={cx(
                  "property-value",
                  stateValue === undefined && "not-defined"
                )}
                aria-labelledby={`${name}-${stateName}`}
              >
                {stateValue !== undefined
                  ? JSON.stringify(stateValue)
                  : "undefined"}
              </pre>
            </li>
          )
        )}
      </ul>
    </>
  );
}

function PropertyListItem({
  componentId,
  propertyName,
  propertyValue,
}: {
  componentId: string;
  propertyName: string;
  propertyValue: any;
}) {
  const labelId = useRef(`${componentId}-${propertyName}`);

  return (
    <li className={cx("property-list-item")}>
      <label
        className={cx("property-label")}
        id={labelId.current}
      >
        {propertyName}
      </label>
      <pre
        className={cx("property-value")}
        aria-labelledby={labelId.current}
      >
        {propertyValue !== undefined
          ? JSON.stringify(propertyValue)
          : "undefined"}
      </pre>
    </li>
  );
}

export const ReactHookFormDevtools = (
  props: HTMLAttributes<HTMLDivElement>
) => {
  const { control, formControl } = useReactHookFormDevtools();

  if (!control || !formControl)
    return (
      <>
        <div
          className={cx("content-container", props.className)}
          aria-label="React Hook Form Development Tools Panel"
        >
          <div className={cx("content-body")}>
            <EmptyState
              icon={<Icon variant="gear-fine" />}
              title="No form instance connected"
              suggestion="Instantiate a form with useFormInspect() instead of useForm() somewhere in the codebase to see the details of the form when it's rendered here."
              className={cx("empty-state")}
            />
          </div>
        </div>
      </>
    );

  return <ReactHookFormDevtoolsContent {...props} />;
};
